import CustomHTMLElement from '@onpace/onspace-core/components/html_element'

import translation from '@onpace/onspace-core/components/translations'
import OnspacePlayerBar from '@onpace/onspace-media/elements/player/bar'
import OnspacePlayerTabs from '@onpace/onspace-media/elements/player/tabs'

const PLAYBACK_RATES = [0.5, 1, 1.25, 1.5, 2]

/// The Onspace Player Controls is an element to control a Media Player.
///
/// This class should not be used directly, instead subclass it and override the functionality as required.
export default class OnspacePlayerControls extends CustomHTMLElement {
  runConstructor(options = {}) {
    super.runConstructor(options)

    this.resizeObserver = new ResizeObserver(this.resizeObserved.bind(this))
  }

  /// Runs when the controls element is first connected to the DOM.
  runFirstConnected(options = {}) {
    super.runFirstConnected(options)

    this.classList.add('onspace-player-controls')

    this.addPressEventListeners({
      onPress: this.pressed.bind(this),
      onLongPress: this.longPressed.bind(this)
    })

    this.player = options.player
    this._playerEvents = {}
    this.attachPlayerEvents(this.player)

    this.setupStructure()
    this.setupInitialContent()
    if (this.player.playbackStarted) {
      this.setupPlaybackContent()
    }

    this.detectDebugMode()
  }

  /// Runs when the controls are connected to the DOM.
  runConnected() {
    super.runConnected()

    this.resizeObserver.observe(this)
    this.setupKeyboard()
  }

  /// Runs when the controls are disconnected from the DOM.
  runDisconnected() {
    super.runDisconnected()

    if (this.resizeObserver) { this.resizeObserver.unobserve(this) }
    this.cleanupKeyboard()
  }

  /// Responds to changes in the element's size.
  resizeObserved(_entries) {
    const style = this.getComputedStyle()
    this.horizontalBreakpoint = style.getPropertyValue('--player-controls-breakpoint-horizontal')
    this.verticalBreakpoint = style.getPropertyValue('--player-controls-breakpoint-horizontal')

    if (!this.player.playbackStarted) { return }

    this.updateMenuControls()
    this.hideMenu()
  }

  /// Attaches a different player to the controls.
  attachPlayer(player) {
    if (this.player == player) { return }

    this.detachPlayerEvents(this.player)

    this.player = player
    this.attachPlayerEvents(player)
    this.playerDidChange()
  }

  ////////// Interaction

  ///// Press

  /// Responds to clicks and touches on the controls.
  pressed(_event) {}

  /// Responds to long clicks and touches on the element.
  ///
  /// This enables debug mode, if it is not already.
  longPressed(_event) {
    this.toggleDebugMode()
  }

  ///// Keyboard

  /// Sets up the events required for using the keyboard.
  setupKeyboard() {
    this.keysDown = []
    this.addDocumentBoundEventListener('keydown', this.documentKeyDowned)
    this.addDocumentBoundEventListener('keyup', this.documentKeyUpped)
  }

  /// Cleans up the events required for using the keyboard.
  cleanupKeyboard() {
    this.removeDocumentBoundEventListener('keydown')
    this.removeDocumentBoundEventListener('keyup')
  }

  /// Callback for keyboard presses on the document.
  ///
  /// This checks and ignores any repeated key presses until the key is lifted. It then delegates functionality to
  /// +keyPressBegan+.
  ///
  /// Note that this will only be triggered when the menu is active.
  documentKeyDowned(event) {
    if (this.keysDown.includes(event.key)) { return }

    this.keysDown.push(event.key)

    this.keyPressBegan(event)
  }

  /// Callback for keyboard releases on the document.
  ///
  /// This delegates functionality to +keyPressEnded+.
  documentKeyUpped(event) {
    const index = this.keysDown.indexOf(event.key)
    this.keysDown.splice(index, 1)

    this.keyPressEnded(event)
  }

  /// Responds to keyboard presses beginning on the document.
  keyPressBegan(event) {
    if (this.playingAdvertisement) { return }

    switch (event.key) {
    case ' ':
      this.player.togglePlayback()
      break
    case 'Escape':
      if (!this.handleEscapeKeyPress()) {
        return
      }

      break
    case 'ArrowLeft':
      this.player.beginRepeatedlyAdjustingPosition(-10, 250)
      break
    case 'ArrowRight':
      this.player.beginRepeatedlyAdjustingPosition(10, 250)
      break
    case 'ArrowDown':
      this.player.beginRepeatedlyAdjustingAudioVolume(-0.1, 250)
      break
    case 'ArrowUp':
      this.player.beginRepeatedlyAdjustingAudioVolume(0.1, 250)
      break
    case 'd':
      this.dKeyPressTimeout = setTimeout(() => this.toggleDebugMode(), 3000)
      break
    case 'm':
      this.player.toggleAudio()
      break
    case 's':
      if (this.showingUpNext) {
        this.player.startNextPlayer()
      } else {
        this.player.skipChapter()
      }
      break
    default:
      return
    }

    event.preventDefault()
    event.stopPropagation()
    return false
  }

  /// Responds to keyboard presses ending on the document.
  keyPressEnded(event) {
    switch (event.key) {
    case 'ArrowLeft':
    case 'ArrowRight':
      this.player.endRepeatedlyAdjustingPosition()
      break
    case 'ArrowDown':
    case 'ArrowUp':
      this.player.endRepeatedlyAdjustingAudioVolume()
      break
    case 'd':
      clearTimeout(this.dKeyPressTimeout)
      this.dKeyPressTimeout = null
      break
    default:
      return
    }

    event.preventDefault()
    event.stopPropagation()
    return false
  }

  /// Responds to keyboard presses on the escape key.
  ///
  /// This returns +true+ if the event was handled.
  handleEscapeKeyPress() {
    if (this.tabsElement && this.tabsElement.expanded) {
      this.tabsElement.selectedTabIndex = null
    } else if (this.menuVisible) {
      this.hideMenu()
    } else if (this.showingUpNext) {
      this.destroyUpNext()
    } else if (this.player.hasCloseAction) {
      this.player.performCloseAction()
    } else {
      return false
    }

    return true
  }

  ////////// Events

  /// Adds an event listener for the given player.
  ///
  /// This will bind the listener to the instance, and store it for later.
  addPlayerEventListener(player, event, listener) {
    const boundListener = listener.bind(this)
    this._playerEvents[event] = boundListener
    player.addEventListener(event, boundListener)
  }

  /// Removes an existing event listener from the given player.
  removePlayerEventListener(player, event) {
    if (!this._playerEvents[event]) { return }

    player.removeEventListener(event, this._playerEvents[event])
    delete this._playerEvents[event]
  }

  /// Attaches the event listeners for the given player.
  attachPlayerEvents(player) {
    this.addPlayerEventListener(player, 'onspace:media:player:metadata-changed', this.playerMetadataChanged)
    this.addPlayerEventListener(player, 'onspace:media:player:close-action-changed', this.playerCloseActionChanged)
    this.addPlayerEventListener(player, 'onspace:media:player:loading-changed', this.playerLoadingChanged)
    this.addPlayerEventListener(player, 'onspace:media:player:playback-started', this.playerPlaybackStarted)
    this.addPlayerEventListener(player, 'onspace:media:player:playback-changed', this.playerPlaybackChanged)
    this.addPlayerEventListener(player, 'onspace:media:player:playback-rate-changed', this.playerPlaybackRateChanged)
    this.addPlayerEventListener(player, 'onspace:media:player:position-changed', this.playerPositionChanged)
    this.addPlayerEventListener(player, 'onspace:media:player:duration-changed', this.playerDurationChanged)
    this.addPlayerEventListener(player, 'onspace:media:player:chapter-changed', this.playerChapterChanged)
    this.addPlayerEventListener(player, 'onspace:media:player:audio-tracks-changed', this.playerAudioTracksChanged)
    this.addPlayerEventListener(player, 'onspace:media:player:volume-changed', this.playerVolumeChanged)
    this.addPlayerEventListener(player, 'onspace:media:player:text-tracks-changed', this.playerTextTracksChanged)
    this.addPlayerEventListener(player, 'onspace:media:player:tabs-changed', this.playerTabsChanged)
    this.addPlayerEventListener(player, 'onspace:media:player:media-changed', this.playerMediaChanged)
    this.addPlayerEventListener(player, 'onspace:media:player:remote-changed', this.playerRemoteChanged)
    this.addPlayerEventListener(player, 'onspace:media:player:advertisement-changed', this.playerAdvertisementChanged)
    this.addPlayerEventListener(player, 'onspace:media:player:advertisement-position-changed', this.playerAdvertisementPositionChanged)
    this.addPlayerEventListener(player, 'onspace:media:player:up-next', this.playerPublishedUpNext)
  }

  /// Removes the event listeners from the given player.
  detachPlayerEvents(player) {
    this.removePlayerEventListener(player, 'onspace:media:player:metadata-changed')
    this.removePlayerEventListener(player, 'onspace:media:player:close-action-changed')
    this.removePlayerEventListener(player, 'onspace:media:player:loading-changed')
    this.removePlayerEventListener(player, 'onspace:media:player:playback-started')
    this.removePlayerEventListener(player, 'onspace:media:player:playback-changed')
    this.removePlayerEventListener(player, 'onspace:media:player:playback-rate-changed')
    this.removePlayerEventListener(player, 'onspace:media:player:position-changed')
    this.removePlayerEventListener(player, 'onspace:media:player:duration-changed')
    this.removePlayerEventListener(player, 'onspace:media:player:chapter-changed')
    this.removePlayerEventListener(player, 'onspace:media:player:audio-tracks-changed')
    this.removePlayerEventListener(player, 'onspace:media:player:volume-changed')
    this.removePlayerEventListener(player, 'onspace:media:player:text-tracks-changed')
    this.removePlayerEventListener(player, 'onspace:media:player:tabs-changed')
    this.removePlayerEventListener(player, 'onspace:media:player:media-changed')
    this.removePlayerEventListener(player, 'onspace:media:player:remote-changed')
    this.removePlayerEventListener(player, 'onspace:media:player:advertisement-changed')
    this.removePlayerEventListener(player, 'onspace:media:player:advertisement-position-changed')
    this.removePlayerEventListener(player, 'onspace:media:player:up-next')
  }

  ////////// Player

  /// Responds to the control instance's player changing.
  playerDidChange() {
    this.updateActionControls()
    this.updateMetadataElement()
    this.updatePlaybackControls()
    this.updateAudioElements()
    this.updateRemoteControls()
    this.updatePositionElement()

    if (this.tabsElement) { this.tabsElement.player = this.player }
    this.updateTabsElement()
  }

  /// Responds to the player changing its metadata.
  playerMetadataChanged(_event) {
    this.updateMetadataElement()
  }

  /// Responds to the player changing its close action.
  playerCloseActionChanged(_event) {
    this.updateActionControls()
  }

  /// Responds to the player changin its loading state.
  playerLoadingChanged(_event) {
    this.updatePlaybackControls()
  }

  /// Responds to the player beginning playback.
  playerPlaybackStarted(_event) {
    this.destroyUpNext()
    this.setupPlaybackContent()
    this.updateActionControls()
  }

  /// Responds to the playback state of the player changing.
  playerPlaybackChanged(_event) {
    this.updatePlaybackControls()
    this.detectPositionScrubberAnimation()
  }

  /// Responds to the playback rate of the player changing.
  playerPlaybackRateChanged(_event) {
    this.updatePlaybackControls()
  }

  /// Responds to the playback position of the player changing.
  playerPositionChanged(_event) {
    this.updatePositionElement()
  }

  /// Responds to the total duration of the player changing.
  playerDurationChanged(_event) {
    this.updatePlaybackControls()
    this.updatePositionElement()
  }

  /// Responds to the current chapter of the player changing.
  playerChapterChanged(_event) {
    this.updateActionControls()
  }

  /// Responds to a change in the player's audio tracks.
  playerAudioTracksChanged(_event) {
    this.updateAudioElements()
  }

  /// Responds to the volume of the player changing.
  playerVolumeChanged(_event) {
    this.updateAudioElements()
  }

  /// Responds to a change in the player's text tracks.
  playerTextTracksChanged(_event) {
    this.updateAudioElements()
  }

  /// Responds to the player's tabs changing.
  playerTabsChanged(_event) {
    this.updateTabsElement()
  }

  /// Responds to the player changing media, re-filtering the tabs.
  playerMediaChanged(_event) {
    this.updateTabsElement()
  }

  /// Responds to the remote state of the player changing.
  playerRemoteChanged(_event) {
    this.updateRemoteControls()
  }

  /// Responds to the player changing the current advertisement.
  playerAdvertisementChanged(_event) {
    this.updateMetadataElement()
    this.updateActionControls()
    this.updatePlaybackControls()
    this.updatePositionElement()
  }

  /// Responds to the player the position of the playing advertisement.
  playerAdvertisementPositionChanged(_event) {
    this.updateActionControls()
  }

  /// Responds to the player publishing media to play next.
  playerPublishedUpNext(_event) {
    this.setupUpNext()
  }

  ////////// Structure

  /// Creates the structural elements for the controls.
  setupStructure() {
    this.headerElement = document.createElement('div')
    this.headerElement.classList.add('onspace-player-controls__header')
    this.appendChild(this.headerElement)

    this.centerElement = document.createElement('div')
    this.centerElement.classList.add('onspace-player-controls__center')
    this.appendChild(this.centerElement)

    this.footerElement = document.createElement('div')
    this.footerElement.classList.add('onspace-player-controls__footer')
    this.appendChild(this.footerElement)
  }

  /// Sets up the content which should be displayed at all times.
  setupInitialContent() {
    this.setupMetadata()
    this.setupBars()
  }

  /// Sets up the content which should only be displayed after playback has begun.
  setupPlaybackContent() {
    this.setupMenuControls()
    this.setupActionControls()
    this.setupPlaybackControls()
    this.setupAudioControls()
    this.setupRemoteControls()
    this.setupPositionControls()
    this.setupTabsElement()
  }

  ////////// Metadata

  /// Initialises the metadata element.
  setupMetadata() {
    this.updateMetadataElement()
  }

  /// Updates the metadata element.
  updateMetadataElement() {
    if (this.metadataElement) { this.metadataElement.remove() }

    if (this.player.playingAdvertisement) {
      if (typeof this.player.advertisementCallback === 'function') {
        this.metadataElement = document.createElement('a')
        this.metadataElement.addPressEventListeners({ onPress: () => this.player.performAdvertisementCallback() })
      } else {
        this.metadataElement = document.createElement('div')
      }

      this.metadataElement.classList.add('onspace-player-controls__metadata--advertisement')
    } else {
      this.metadataElement = document.createElement('div')
    }

    this.metadataElement.classList.add('onspace-player-controls__metadata')

    const metadata = this.player.metadata || {}

    if (metadata.artwork) {
      const artworkElement = document.createElement('img')
      artworkElement.src = metadata.artwork
      this.metadataElement.appendChild(artworkElement)
    }

    const contentElement = document.createElement('div')

    if (this.player.playingAdvertisement) {
      const sponsoredElement = document.createElement('span')
      sponsoredElement.classList.add('onspace-player-controls__metadata__sponsored')
      sponsoredElement.innerText = translation('onspace.media.player.advertisement.sponsored')
      contentElement.appendChild(sponsoredElement)
    }

    if (metadata.title) {
      const titleElement = document.createElement('span')
      titleElement.classList.add('onspace-player-controls__metadata__title')
      titleElement.innerText = metadata.title
      contentElement.appendChild(titleElement)
    }

    if (metadata.subtitle) {
      const subtitleElement = document.createElement('div')
      subtitleElement.classList.add('onspace-player-controls__metadata__subtitle')
      subtitleElement.innerText = metadata.subtitle
      contentElement.appendChild(subtitleElement)
    }

    this.metadataElement.appendChild(contentElement)

    if (this.metadataElement.tagName === 'A') {
      const clickoutElement = SVGElement.createOnspaceSpritemapSvg('onspace/player_clickout')
      this.metadataElement.appendChild(clickoutElement)
    }

    this.centerElement.prepend(this.metadataElement)
  }

  ////////// Up Next

  /// Initialises and displays the up next element.
  setupUpNext() {
    if (this.upNextElement) { return }

    this.upNextElement = document.createElement('div')
    this.upNextElement.classList.add('onspace-player-controls__upnext')
    this.upNextElement.addPressEventListeners({ onPress: this.upNextPressed.bind(this) })

    const headerBarElement = new OnspacePlayerBar()
    this.upNextElement.appendChild(headerBarElement)

    const titleElement = document.createElement('div')
    titleElement.classList.add('onspace-player-bar__text')
    titleElement.innerText = `${translation('onspace.media.player.up_next.title')}:`
    headerBarElement.appendChild(titleElement)

    headerBarElement.addButton('close', { icon: 'onspace/icon_cross', pressed: this.upNextCloseButtonPressed.bind(this) })

    const contentElement = document.createElement('a')
    contentElement.classList.add('onspace-player-controls__upnext__content')
    this.upNextElement.appendChild(contentElement)

    const metadata = this.player.upNextMetadata || {}

    if (metadata.artwork) {
      const artworkElement = document.createElement('img')
      artworkElement.src = metadata.artwork
      contentElement.appendChild(artworkElement)
    }

    const textElement = document.createElement('div')
    textElement.classList.add('onspace-player-controls__upnext__text')

    if (metadata.title) {
      const titleElement = document.createElement('span')
      titleElement.classList.add('onspace-player-controls__upnext__text__title')
      titleElement.innerText = metadata.title
      textElement.appendChild(titleElement)
    }

    if (metadata.subtitle) {
      const subtitleElement = document.createElement('span')
      subtitleElement.classList.add('onspace-player-controls__upnext__text__subtitle')
      subtitleElement.innerText = metadata.subtitle
      textElement.appendChild(subtitleElement)
    }

    if (textElement.children.length > 0) {
      contentElement.appendChild(textElement)
    }

    this.upNextButtonBackground = document.createElement('div')
    this.upNextButtonBackground.classList.add('onspace-player-controls__upnext__background')
    contentElement.appendChild(this.upNextButtonBackground)

    const automaticPlayInterval = this.player.upNextAutomaticDuration
    if (typeof automaticPlayInterval === 'number' && automaticPlayInterval > 0) {
      this.upNextButtonBackground.style.width = '0%'
      this.upNextButtonBackground.style.transition = `width ${automaticPlayInterval}s linear`

      this.upNextAutomaticCallback = () => {
        this.player.startNextPlayer()
      }
      this.upNextButtonBackground.addEventListener('transitionend', this.upNextAutomaticCallback)

      setTimeout(() => {
        this.upNextButtonBackground.style.width = ''
      })
    }

    this.centerElement.appendChild(this.upNextElement)
  }

  /// Indicates if the up next UI is currently visible.
  get showingUpNext() {
    return !!this.upNextElement
  }

  /// Cancels the logic which wil automatically begin the next media.
  cancelUpNextAutomatic() {
    if (!this.upNextAutomaticCallback) { return }

    this.upNextButtonBackground.removeEventListener('transitionend', this.upNextAutomaticCallback)
    this.upNextButtonBackground.style.width = ''
    this.upNextButtonBackground.style.transition = ''

    this.upNextAutomaticCallback = null
  }

  /// Removes and cleans up the up next element.
  destroyUpNext() {
    if (!this.upNextElement) { return }

    this.cancelUpNextAutomatic()

    this.upNextElement.remove()
    this.upNextElement = null
    this.upNextButtonBackground = null
    this.upNextAutomaticCallback = null
  }

  ///// Events

  /// Responds to presses on the up next element.
  upNextPressed(_event) {
    this.player.startNextPlayer()
  }

  /// Responds to presses on the up next element's close button.
  upNextCloseButtonPressed(_event) {
    this.destroyUpNext()
  }

  ////////// Bars

  /// Initialises the bars.
  setupBars() {
    if (this.barLeftElement) { return }

    [this.headerBarElement, this.barLeftElement, this.barRightElement] = this.createBars()

    this.headerElement.appendChild(this.headerBarElement)

    const barsElement = document.createElement('div')
    barsElement.classList.add('onspace-player-controls__bars')
    this.footerElement.appendChild(barsElement)

    barsElement.appendChild(this.barLeftElement)
    barsElement.appendChild(this.barRightElement)
  }

  /// Creates the bar elements.
  createBars() {
    const headerBarElement = new OnspacePlayerBar()
    headerBarElement.classList.add('onspace-player-bar--header')

    const barLeftElement = new OnspacePlayerBar()
    barLeftElement.classList.add('onspace-player-bar--left')

    const barRightElement = new OnspacePlayerBar()
    barRightElement.classList.add('onspace-player-bar--right')

    return [headerBarElement, barLeftElement, barRightElement]
  }

  ////////// Menu

  /// Initialises the elements for the overlay menu.
  setupMenuControls() {
    if (this.moreButton) { return }

    this.moreButton = this.barRightElement.addButton('more', { icon: 'onspace/icon_ellipsis', pressed: this.morePressed.bind(this) })
    this.moreButtonItem = {
      icon: 'onspace/icon_ellipsis',
      title: translation('onspace.media.player.controls.more'),
      button: this.moreButton
    }

    this.moreButtonItems = []
    this.availableMoreItems = []

    this.updateMenuControls()
  }

  /// Updates the elements for the overlay menu.
  updateMenuControls() {
    if (this.horizontalBreakpoint === 'compact' && this.availableMoreItems.length > 1) {
      this.moreButton.style.display = ''

      this.moreButtonItems.forEach((item) => {
        item.button.style.display = 'none'
      })
    } else {
      this.moreButton.style.display = 'none'

      this.moreButtonItems.forEach((item) => {
        if (item.hidden) {
          item.button.style.display = 'none'
        } else {
          item.button.style.display = ''
        }
      })
    }
  }

  /// Updates the list of items available in the more menu.
  updateAvailableMenuItems() {
    this.availableMoreItems = this.moreButtonItems.filter((item) => !item.hidden)
    this.updateMenuControls()
  }

  /// Creates a button which will be shown in the right bar and more menu.
  ///
  /// When at a condensed screen size, all "more" buttons will be condensed into a single button at the end of the bar.
  /// This requires a unique +name+ to reference it by, a +title+ for the menu, and supports the following options:
  /// [icon]
  ///   The name of the icon to give the button, menu item and menu.
  /// [pressed]
  ///   A function which will be called when the button or item is pressed.
  ///
  /// The returned element supports the following attributes:
  /// [setIcon]
  ///   Updates the icon displayed on the button and menu item.
  /// [setButtonIcon]
  ///   Updates the icon displayed only on the button.
  /// [hidden]
  ///   Indicates if the button and item should be hidden. This will take into account the current screen size when
  ///   choosing what to display.
  addMoreMenuItem(name, title, options = {}) {
    const button = this.barRightElement.addButton(name, { icon: options.icon, pressed: options.pressed })

    const item = {
      icon: options.icon,
      title,
      button,
      pressed: options.pressed
    }
    Object.defineProperty(item, 'hidden', {
      get: () => {
        return item._hidden
      },
      set: (value) => {
        item._hidden = value

        this.updateAvailableMenuItems()
      }
    })
    item._hidden = false

    item.setIcon = (icon) => {
      button.setIcon(icon)
      item.icon = icon
    }
    item.setButtonIcon = (icon) => {
      button.setIcon(icon)
    }

    this.moreButtonItems.push(item)
    this.updateAvailableMenuItems()

    return item
  }

  /// Indicates if the menu overlay is visible.
  get menuVisible() {
    return !!this.menuElement
  }

  /// Displays a menu overlay with the given items.
  ///
  /// The menu is positioned relative to the given +button+ element, and the rest of the controls will be disabled. If
  /// the menu is already visible, the position will not change. The +items+ should be an array objects with the
  /// following parameters:
  /// [title]
  ///   Text to display in the item.
  /// [icon]
  ///   An icon to display in the item.
  /// [pressed]
  ///   A function which will be called when the item is pressed.
  ///
  /// Additionally, the following options are available:
  /// [keepVisible]
  ///   By default, the menu will be hidden when an item is selected. Setting this to +true+ will keep it visible.
  showMenu(menuItem, items, options = {}) {
    let showHeader

    if (!this.menuElement) {
      this.menuElement = document.createElement('div')
      this.menuElement.classList.add('onspace-player-controls__menu')
      this.menuElement.addPressEventListeners({ onPress: this.menuBackgroundPressed.bind(this) })

      this.menuElement.initialItem = menuItem
      menuItem.button.highlighted = true

      const buttonRect = menuItem.button.getBoundingClientRect()
      const controlsRect = this.getBoundingClientRect()
      this.menuElement.style.setProperty('--player-menu-offset-right', `${controlsRect.right - buttonRect.right}px`)
      this.menuElement.style.setProperty('--player-menu-offset-bottom', `${controlsRect.bottom - buttonRect.top}px`)
      this.menuElement.style.setProperty('--player-menu-offset-height', `${buttonRect.top - controlsRect.top}px`)

      this.appendChild(this.menuElement)

      showHeader = false
    } else {
      showHeader = true
    }

    this.menuElement.innerHTML = ''

    const menuContainer = document.createElement('div')
    this.menuElement.appendChild(menuContainer)

    if (showHeader) {
      const headerElement = document.createElement('div')
      headerElement.classList.add('onspace-player-controls__menu__header')
      menuContainer.appendChild(headerElement)

      const headerSvg = SVGElement.createOnspaceSpritemapSvg(menuItem.icon)
      headerElement.appendChild(headerSvg)

      const headerTitle = document.createElement('span')
      headerTitle.innerText = menuItem.title
      headerElement.appendChild(headerTitle)
    }

    const itemsElement = document.createElement('div')
    itemsElement.classList.add('onspace-player-controls__menu__items')
    menuContainer.appendChild(itemsElement)

    items.forEach((item) => {
      const itemElement = document.createElement('div')
      itemElement.classList.add('onspace-player-controls__menu__item')
      itemElement.addPressEventListeners({ onPress: () => {
        item.pressed()
        if (!options.keepVisible) { this.hideMenu() }
      } })

      if (typeof item.icon === 'string') {
        const svgElement = SVGElement.createOnspaceSpritemapSvg(item.icon)
        itemElement.appendChild(svgElement)
      }

      if (item.title) {
        const titleElement = document.createElement('span')
        titleElement.classList.add('onspace-player-controls__menu__item__title')
        titleElement.innerText = item.title
        itemElement.appendChild(titleElement)
      }

      itemsElement.appendChild(itemElement)
    })
  }

  /// Hides and removes the menu overlay.
  hideMenu() {
    if (!this.menuElement) { return }

    this.menuElement.initialItem.button.highlighted = false

    this.menuElement.remove()
    this.menuElement = null
  }

  ///// Events

  /// Responds to presses on the more button.
  morePressed(_event) {
    const items = this.availableMoreItems.map((item) => {
      return { title: item.title, icon: item.icon, pressed: item.pressed }
    })
    this.showMenu(this.moreButtonItem, items, { keepVisible: true })
  }

  /// Responds to presses on the background element of the menu.
  ///
  /// This closes the menu.
  menuBackgroundPressed(_event) {
    this.hideMenu()
  }

  //////////

  /// Initialises the elements for custom action buttons.
  setupActionControls() {
    if (this.closeButton) { return }

    this.closeButton = this.headerBarElement.addButton('close', { icon: 'onspace/icon_cross', pressed: this.closePressed.bind(this) })

    this.skipButton = document.createElement('a')
    this.skipButton.classList.add('onspace-player-controls__skip')
    this.skipButton.addPressEventListeners({ onPress: this.skipActionPressed.bind(this) })
    this.centerElement.appendChild(this.skipButton)

    this.updateActionControls()
  }

  /// Updates the elements for action controls.
  updateActionControls() {
    if (!this.closeButton) { return }

    if (this.player.hasCloseAction) {
      this.closeButton.style.display = ''
    } else {
      this.closeButton.style.display = 'none'
    }

    if (this.player.advertisementSkipAvailable) {
      this.skipButton.style.display = ''
      if (this.player.advertisementCanSkipNow) {
        this.skipButton.removeAttribute('disabled')
        this.skipButton.innerText = translation('onspace.media.player.advertisement.continue_skip')
      } else {
        this.skipButton.setAttribute('disabled', '')
        this.skipButton.innerText = `${translation('onspace.media.player.advertisement.continue_skip_in')} ${Math.round(this.player.advertisementDurationUntilSkip)}`
      }
    } else if (this.player.canSkipChapter) {
      this.skipButton.style.display = ''
      this.skipButton.innerText = translation(`onspace.media.player.chapter.skip.${this.player.currentChapter.type}`)
    } else {
      this.skipButton.style.display = 'none'
    }
  }

  ///// Events

  /// Responds to clicks on the close button.
  closePressed() {
    this.player.performCloseAction()
  }

  /// Responds to clicks on the skip action button.
  skipActionPressed() {
    if (this.skipButton.getBooleanAttribute('disabled')) { return }

    if (this.player.advertisementSkipAvailable) {
      if (this.player.advertisementCanSkipNow) { this.player.advertisementCompleted() }
    } else if (this.player.canSkipChapter) {
      this.player.skipChapter()
    }
  }

  ////////// Playback

  /// Initialises the elements which control playback.
  setupPlaybackControls() {
    if (this.playPauseButton) { return }

    this.skipBackwardButton = this.barLeftElement.addButton('skip-backward', { icon: 'onspace/player_skip_backward', pressBegan: this.skipBackwardPressBegan.bind(this), pressEnded: this.skipBackwardPressEnded.bind(this) })
    this.playPauseButton = this.barLeftElement.addButton('play-pause', { icon: 'onspace/player_play', pressed: this.playPausePressed.bind(this) })
    this.skipForwardButton = this.barLeftElement.addButton('skip-forward', { icon: 'onspace/player_skip_forward', pressBegan: this.skipForwardPressBegan.bind(this), pressEnded: this.skipForwardPressEnded.bind(this) })

    this.loadingIndicator = this.barLeftElement.addIndicator('loading', { icon: 'onspace/icon_loading' })

    this.playbackRateItem = this.addMoreMenuItem('rate', translation('onspace.media.player.controls.rate.title'), { icon: 'onspace/player_speed', pressed: this.playbackRatePressed.bind(this) })

    this.updatePlaybackControls()
  }

  /// Updates the state of playback controls.
  updatePlaybackControls() {
    if (!this.playPauseButton || !this.liveIndicatorElement) { return }

    if (this.player.isLiveContent) {
      this.classList.add('onspace-player-controls--live')

      this.skipBackwardButton.style.display = 'none'
      this.skipForwardButton.style.display = 'none'

      if (this.player.hideLivePlaybackControls) {
        this.playPauseButton.style.display = 'none'
        this.liveIndicatorElement.style.marginLeft = '0'
      } else {
        this.playPauseButton.style.display = ''
        this.liveIndicatorElement.style.marginLeft = ''
      }
    } else if (this.player.playingAdvertisement) {
      this.playPauseButton.style.display = ''
      this.skipBackwardButton.style.display = 'none'
      this.skipForwardButton.style.display = 'none'
    } else {
      this.classList.remove('onspace-player-controls--live')
      this.playPauseButton.style.display = ''
      this.skipBackwardButton.style.display = ''
      this.skipForwardButton.style.display = ''
    }

    if (this.player.isPlaying) {
      this.playPauseButton.setIcon('onspace/player_pause')
    } else {
      this.playPauseButton.setIcon('onspace/player_play')
    }

    if (this.player.isLoading) {
      this.loadingIndicator.style.display = ''
    } else {
      this.loadingIndicator.style.display = 'none'
    }

    if (this.player.canChangePlaybackRate) {
      this.playbackRateItem.hidden = false

      if (this.player.playbackRate === 1) {
        this.playbackRateItem.setButtonIcon('onspace/player_speed')
      } else {
        this.playbackRateItem.setButtonIcon('onspace/player_speed_active')
      }
    } else {
      this.playbackRateItem.hidden = true
    }
  }

  ///// Events

  /// Responds to clicks on the play/pause button.
  playPausePressed(_event) {
    this.player.togglePlayback()
  }

  /// Responds to a press on the skip backward button starting.
  skipBackwardPressBegan(_event) {
    this.player.beginRepeatedlyAdjustingPosition(-10, 250)
  }

  /// Responds to a press on the skip backward button finishing.
  skipBackwardPressEnded(_event) {
    this.player.endRepeatedlyAdjustingPosition()
  }

  /// Responds to a press on the skip forward button starting.
  skipForwardPressBegan(_event) {
    this.player.beginRepeatedlyAdjustingPosition(10, 250)
  }

  /// Responds to a press on the skip forward button ending.
  skipForwardPressEnded(_event) {
    this.player.endRepeatedlyAdjustingPosition()
  }

  /// Responds to a press on the playback rate button or more item.
  playbackRatePressed(_event) {
    const items = PLAYBACK_RATES.map((rate) => {
      return {
        title: translation(`onspace.media.player.controls.rate.${rate.toString().replace('.', '_')}`),
        icon: this.player.playbackRate === rate ? 'onspace/icon_checkmark' : '',
        pressed: () => { this.playbackRateSelected(rate) }
      }
    })
    this.showMenu(this.playbackRateItem, items)
  }

  /// Responds to a selection in the playback rate menu.
  playbackRateSelected(value) {
    this.player.playbackRate = value
  }

  ////////// Audio

  /// Initialises the elements which control audio.
  setupAudioControls() {
    if (this.volumeButton) { return }

    this.volumeButton = this.barRightElement.addDropdownSlider('volume', { icon: 'onspace/player_volume_muted', pressed: this.volumePressed.bind(this), changed: this.volumeChanged.bind(this) })
    this.audioTrackItem = this.addMoreMenuItem('audio', translation('onspace.media.player.controls.audio'), { icon: 'onspace/player_audio', pressed: this.audioTrackPressed.bind(this) })
    this.textTrackItem = this.addMoreMenuItem('text', translation('onspace.media.player.controls.text.title'), { icon: 'onspace/player_subtitles', pressed: this.textTrackPressed.bind(this) })

    this.updateAudioElements()
  }

  /// Updates the state of audio elements.
  updateAudioElements() {
    if (!this.volumeButton) { return }

    const volume = this.player.audioVolume

    if (this.player.canControlAudio) {
      this.volumeButton.style.display = ''

      if (!this.player.audioEnabled) {
        this.volumeButton.setIcon('onspace/player_volume_muted')
      } else if (volume > 0.6) {
        this.volumeButton.setIcon('onspace/player_volume_loud')
      } else if (volume > 0.3) {
        this.volumeButton.setIcon('onspace/player_volume_normal')
      } else {
        this.volumeButton.setIcon('onspace/player_volume_low')
      }

      this.volumeButton.value = volume
    } else {
      this.volumeButton.style.display = 'none'
    }

    if (this.player.audioTracks.length > 1) {
      this.audioTrackItem.hidden = false
    } else {
      this.audioTrackItem.hidden = true
    }

    if (this.player.textTracks.length > 0) {
      this.textTrackItem.hidden = false

      if (this.player.textEnabled) {
        this.textTrackItem.setButtonIcon('onspace/player_subtitles_active')
      } else {
        this.textTrackItem.setButtonIcon('onspace/player_subtitles')
      }
    } else {
      this.textTrackItem.hidden = true
    }
  }

  /// Creates a new volume element.
  createVolumeControl() {
    const volumeElement = document.createElement('div')
    volumeElement.classList.add('onspace-player-controls__volume')

    const scrubberElement = document.createElement('div')
    scrubberElement.classList.add('onspace-player-controls__volume__scrubber')
    volumeElement.appendChild(scrubberElement)

    const trackElement = document.createElement('div')
    trackElement.classList.add('onspace-player-controls__volume__scrubber__track')
    scrubberElement.appendChild(trackElement)

    return [volumeElement, scrubberElement, trackElement]
  }

  ///// Events

  /// Responds to clicks on the volume button.
  volumePressed(_event) {
    this.player.toggleAudio()
  }

  /// Responds to slides on the volume button.
  volumeChanged(value) {
    this.player.audioVolume = value
  }

  /// Responds to presses on the audio track button or more item.
  audioTrackPressed(_event) {
    const items = this.player.audioTracks.map((track) => {
      return {
        title: track.title,
        icon: track.enabled ? 'onspace/icon_checkmark' : '',
        pressed: () => { this.audioTrackSelected(track.id) }
      }
    })
    this.showMenu(this.audioTrackItem, items)
  }

  /// Responds to a selection in the audio track menu.
  audioTrackSelected(id) {
    this.player.changeAudioTrack(id)
  }

  /// Responds to presses on the text track button or more item.
  textTrackPressed(_event) {
    const items = [
      {
        title: translation('onspace.media.player.controls.text.off'),
        icon: !this.player.textEnabled && !this.player.textAutomaticEnabled ? 'onspace/icon_checkmark' : '',
        pressed: () => { this.player.changeTextTrack() }
      }, {
        title: translation('onspace.media.player.controls.text.auto'),
        icon: this.player.textAutomaticEnabled ? 'onspace/icon_checkmark' : '',
        pressed: () => { this.textTrackSelected({ language: '_auto' }) }
      },
      ...this.player.textTracks.map((track) => {
        return {
          title: track.title,
          icon: track.enabled && !this.player.textAutomaticEnabled ? 'onspace/icon_checkmark' : '',
          pressed: () => { this.textTrackSelected({ id: track.id }) }
        }
      })
    ]
    this.showMenu(this.textTrackItem, items)
  }

  /// Responds to a selection on the text tracks menu.
  textTrackSelected(id) {
    this.player.changeTextTrack(id)
  }

  ////////// Remote

  /// Initialises the elements which control remote playback.
  setupRemoteControls() {
    if (this.airplayButton) { return }

    this.airplayButton = this.barRightElement.addButton('airplay', { icon: 'onspace/player_airplay', pressed: this.airplayPressed.bind(this) })
    this.googleCastButton = this.barRightElement.addButton('google_cast', { icon: 'onspace/player_google_cast', pressed: this.googleCastPressed.bind(this) })

    this.updateRemoteControls()
  }

  /// Updates the state of remote playback controls.
  updateRemoteControls() {
    if (!this.airplayButton) { return }

    if (this.player.airplayActive) {
      this.airplayButton.style.display = ''
      this.airplayButton.setIcon('onspace/player_airplay_active')
    } else if (this.player.canAirplay) {
      this.airplayButton.style.display = ''
      this.airplayButton.setIcon('onspace/player_airplay')
    } else {
      this.airplayButton.style.display = 'none'
    }

    if (this.player.googleCastActive) {
      this.googleCastButton.style.display = ''
      this.googleCastButton.setIcon('onspace/player_google_cast_active')
    } else if (this.player.canGoogleCast) {
      this.googleCastButton.style.display = ''
      this.googleCastButton.setIcon('onspace/player_google_cast')
    } else {
      this.googleCastButton.style.display = 'none'
    }
  }

  ///// Events

  /// Responds to presses on the Airplay button.
  airplayPressed(_event) {
    this.player.selectAirplayDevice()
  }

  /// Responds to presses on the Google Cast button.
  googleCastPressed(_event) {
    this.player.selectGoogleCastDevice()
  }

  ////////// Position

  /// Initialises the position element.
  setupPositionControls() {
    if (this.positionElement) { return }

    [this.positionElement, this.positionScrubberElement, this.positionTrackElement, this.positionElapsedElement, this.positionRemainingElement] = this.createPositionElement()
    this.footerElement.appendChild(this.positionElement)

    this.liveIndicatorElement = this.barLeftElement.addIndicator('live', { text: translation('onspace.media.player.controls.live') })
    this.liveIndicatorElement.style.display = 'none'

    this.positionElement.addPressEventListeners({
      onPressBegan: this.positionScrubberPressBegan.bind(this),
      onPressMoved: this.positionScrubberPressMoved.bind(this),
      onPressEnded: this.positionScrubberPressEnded.bind(this)
    })

    this.updatePlaybackControls()
  }

  /// Creates a new position element.
  createPositionElement() {
    const positionElement = document.createElement('div')
    positionElement.classList.add('onspace-player-controls__position')

    const scrubberElement = document.createElement('div')
    scrubberElement.classList.add('onspace-player-controls__position__scrubber')
    positionElement.appendChild(scrubberElement)

    const backgroundElement = document.createElement('div')
    backgroundElement.classList.add('onspace-player-controls__position__scrubber__background')
    scrubberElement.appendChild(backgroundElement)

    const trackElement = document.createElement('div')
    trackElement.classList.add('onspace-player-controls__position__scrubber__track')
    backgroundElement.appendChild(trackElement)

    const elapsedElement = document.createElement('div')
    elapsedElement.classList.add('onspace-player-controls__position__elapsed')
    elapsedElement.innerText = '00:00'
    positionElement.appendChild(elapsedElement)

    const remainingElement = document.createElement('div')
    remainingElement.classList.add('onspace-player-controls__position__remaining')
    remainingElement.innerText = '00:00'
    positionElement.appendChild(remainingElement)

    return [positionElement, scrubberElement, trackElement, elapsedElement, remainingElement]
  }

  /// Updates the data in the position element.
  updatePositionElement() {
    if (!this.positionElement) { return }

    if (this.player.isLiveContent) {
      this.positionElement.style.display = 'none'
      this.liveIndicatorElement.style.display = ''
    } else {
      this.positionElement.style.display = ''
      this.liveIndicatorElement.style.display = 'none'

      if (this.player.playingAdvertisement) {
        this.positionElement.setAttribute('disabled', '')
      } else {
        this.positionElement.removeAttribute('disabled', '')
      }

      let remaining = this.player.duration - this.player.position
      if (remaining < 0) { remaining = 0 }

      this.positionElapsedElement.innerText = this.player.position.toMediumDurationString()
      this.positionRemainingElement.innerText = `-${remaining.toMediumDurationString()}`

      if (!this.player.isPlaying) {
        this.updatePositionScrubber()
      }
    }
  }

  /// Updates the state of the position scrubber.
  updatePositionScrubber() {
    let percentage

    const duration = this.player.duration
    let position = this.player.position
    if (duration === 0) {
      position = 0
      percentage = 0
    } else {
      percentage = position / duration
    }

    this.positionTrackElement.style.width = `${percentage * 100}%`
  }

  /// Determines if the position scrubber should be animating.
  get canAnimatePositionScrubber() {
    return this.player.isPlaying && !this.player.isLiveContent && this.positionScrubberElement
  }

  /// Detects whether the position scrubber should be updating.
  ///
  /// This will start or stop the animation depending on the desired state.
  detectPositionScrubberAnimation() {
    if (this.canAnimatePositionScrubber) {
      if (!this.positionScrubberAnimation) {
        this.animatePositionScrubber()
      }
    } else if (this.positionScrubberAnimation) {
      cancelAnimationFrame(this.positionScrubberAnimation)
      this.positionScrubberAnimation = null
    }
  }

  /// Smoothly animates the position scrubber during playback.
  ///
  /// This repeatedly calls itself using +window.requestAnimationFrame+, which results in the scrubber being updated for
  /// every repaint of the screen. This will usually match with the screen's frame rate, e.g. 60hz.
  animatePositionScrubber() {
    this.updatePositionScrubber()

    this.positionScrubberAnimation = requestAnimationFrame(this.animatePositionScrubber.bind(this))
  }

  ///// Events

  /// Responds to presses on the position scrubber.
  positionScrubberPressBegan(event) {
    if (this.positionElement.getBooleanAttribute('disabled')) { return }

    document.body.style.setProperty('cursor', 'grabbing', 'important')
    this.positionScrubberElement.classList.add('onspace-player-controls__position__scrubber--scrubbing')

    this.player.beginSeek()

    if (!event.type.startsWith('mouse')) {
      this.scrubberLastTouchX = event.touches[0].clientX
    }

    this.positionScrubberPressMoved(event)
  }

  /// Responds to press movement while scrubbing the position.
  positionScrubberPressMoved(event) {
    event.preventDefault()

    const boundingRect = this.positionScrubberElement.getBoundingClientRect()

    if (event.type.startsWith('mouse')) {
      const percentage = (event.clientX - boundingRect.left) / boundingRect.width
      this.player.position = percentage * this.player.duration
    } else {
      const translationX = event.touches[0].clientX - this.scrubberLastTouchX
      const percentageChange = translationX / boundingRect.width
      this.player.position += percentageChange * this.player.duration

      this.scrubberLastTouchX = event.touches[0].clientX
    }
  }

  /// Responds to a press ending from scrubbing the position.
  positionScrubberPressEnded(_event) {
    this.scrubberLastTouchX = null

    document.body.style.cursor = ''
    this.positionScrubberElement.classList.remove('onspace-player-controls__position__scrubber--scrubbing')

    this.player.endSeek()
  }

  ////////// Tabs

  /// Initialises the tabs element.
  setupTabsElement() {
    if (this.tabsElement) { return }

    this.tabsElement = new OnspacePlayerTabs({ player: this.player })
    this.appendChild(this.tabsElement)

    this.tabsElement.addEventListener('onspace:media:tabs:expanded', this.tabsExpanded.bind(this))
    this.tabsElement.addEventListener('onspace:media:tabs:collapsed', this.tabsCollapsed.bind(this))

    this.updateTabsElement()
  }

  /// Updates the tabs UI.
  updateTabsElement() {
    if (!this.tabsElement) { return }

    this.tabsElement.tabData = [...this.systemTabData, ...this.playerTabData].filter((tab) => tab && this.canDisplayTab(tab))
  }

  ///// Data

  /// Retrieves the tabs provided by the system.
  ///
  /// Any null/blank elements returned by this will be filtered before use.
  get systemTabData() {
    return [this._debugTab, ...this.player.systemTabData]
  }

  /// Retrieves the tabs provided for the media being played.
  ///
  /// Any null/blank elements returned by this will be filtered before use.
  get playerTabData() {
    return this.player.tabData
  }

  /// Indicates if the given tab can be displayed for the media currently being played.
  ///
  /// Tabs without any +mediaIds+ are displayed for every media.
  canDisplayTab(tab) {
    if (!Array.isArray(tab.mediaIds) || tab.mediaIds.length === 0) { return true }

    const mediaId = this.player.metadata && this.player.metadata.id
    return mediaId != null && tab.mediaIds.includes(mediaId)
  }

  ///// Events

  /// Responds to the tabs element showing its content.
  tabsExpanded(_event) {
    this.classList.add('onspace-player-tabs--expanded')
  }

  /// Responds to the tabs element hiding its content.
  tabsCollapsed(_event) {
    this.classList.remove('onspace-player-tabs--expanded')
  }

  ////////// Debug

  /// Detects if debugging mode should be enabled automatically.
  ///
  /// When debugging mode is enabled or disabled, it's state is set in +sessionStorage+. This retrieves that state and
  /// will re-enable debugging mode if it had been for the session.
  detectDebugMode() {
    if (sessionStorage.getItem('onspace-media-player-debug')) {
      this.enableDebugMode()
    }
  }

  /// Toggles debugging mode on or off.
  toggleDebugMode() {
    if (this.debugMode) {
      this.disableDebugMode()
    } else {
      this.enableDebugMode()
    }
  }

  /// Enables debugging mode.
  ///
  /// As well as enabling debugging mode in the current player, it marks it as enabled for the session, so any new
  /// players will also have debug mode enabled.
  ///
  /// This also starts debugging mode, unless playback has not yet started.
  enableDebugMode() {
    if (this.debugMode) { return }

    this.debugMode = true
    sessionStorage.setItem('onspace-media-player-debug', true)

    this._debugTab = {
      title: 'Debug',
      type: 'custom',
      create: this.createDebugElement.bind(this),
      mediaIds: []
    }
    this.updateTabsElement()
  }

  /// Disables debugging mode.
  ///
  /// As well as disabling debugging mode in the current player, it marks it as disabled for the session, so any new
  /// players will also have debug mode disabled.
  disableDebugMode() {
    if (!this.debugMode) { return }

    this.debugMode = false
    sessionStorage.removeItem('onspace-media-player-debug')

    this._debugTab = null
    this.updateTabsElement()
  }

  /// Creates a debug element.
  ///
  /// The default implementation of this raises an error, this should be overridden in a subclass.
  createDebugElement() {
    throw 'This method must be implemented in a subclass'
  }
}
